import React, { useState, useCallback, useEffect, useRef } from 'react';
import { t } from '@/locales/strings';
import { useModalA11y } from '@/hooks/useModalA11y';
import { track } from '@/utils/analytics';
import {
  isTourSeen,
  markTourSeen,
  bumpLater,
} from '@/utils/introState';
import useReducedMotion from '@/hooks/useReducedMotion';

/**
 * FirstRunTour — a single-modal walkthrough shown exactly once to a
 * new user. Two steps:
 *
 *   1. Welcome  — what Be Bold is.
 *   2. Lesson   — one animated illustration that progresses through
 *                 three phases (canal / + flower / + breath) with a
 *                 caption that swaps in time. The lesson plays itself,
 *                 the user can tap to advance phases manually.
 *
 * The earlier 4-step version made the user click through three minor
 * variants of the same SVG; the animated form lands the same lesson
 * in roughly half the panels.
 *
 * State lives in `bb_intro_state` (see src/utils/introState.js):
 *   - `tour_seen`  — true once finished or politely capped.
 *   - `later_n`    — "Maybe later" dismiss count. After LATER_CAP,
 *     the tour is treated as seen so a hesitant user is not
 *     pestered forever.
 *   - `helpcue`    — flipped on tour finish so WeekHome can pulse
 *     the home `?` button once, pointing at the AppGuide.
 *
 * Surface-level orientation lives elsewhere on purpose: the `?`
 * button on the home header opens the re-readable AppGuide, and
 * each bottom-nav route shows a one-time RouteHint banner.
 */
const LATER_CAP = 3;

export const isFirstRunTourSeen = isTourSeen;

export default function FirstRunTour({ open, onClose, lang = 'en' }) {
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = 2;
  const dialogRef = useModalA11y({ open, onClose });
  const startedRef = useRef(false);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (open) {
      setStep(0);
      setPhase(0);
      setPaused(false);
      if (!startedRef.current) {
        startedRef.current = true;
        track('tour_started');
      }
    } else {
      startedRef.current = false;
    }
  }, [open]);

  // Auto-advance through the 3 lesson phases when on the lesson step,
  // unless the user paused (tap-to-pause) or the OS asked us to
  // reduce motion (in which case the user advances with Next).
  useEffect(() => {
    if (!open || step !== 1) return;
    if (paused || reducedMotion) return;
    if (phase >= 2) return;
    const id = setTimeout(() => setPhase(p => Math.min(2, p + 1)), 3200);
    return () => clearTimeout(id);
  }, [open, step, phase, paused, reducedMotion]);

  const finish = useCallback(() => {
    markTourSeen();
    track('tour_completed');
    onClose();
  }, [onClose]);

  // later — the user taps "Maybe later" on step 1. Closes without
  // marking seen, but increments a counter so we cap at LATER_CAP
  // dismisses and stop pestering.
  const later = useCallback(() => {
    const next = bumpLater();
    if (next.later_n >= LATER_CAP) {
      markTourSeen();
      track('tour_dismissed', { reason: 'later_capped', count: next.later_n });
    } else {
      track('tour_dismissed', { reason: 'later', count: next.later_n });
    }
    onClose();
  }, [onClose]);

  const next = useCallback(() => {
    if (step === 0) {
      setStep(1);
      setPhase(0);
      return;
    }
    // On the lesson step: advance phases; once past the last phase,
    // finish.
    if (phase < 2) {
      setPhase(p => p + 1);
      return;
    }
    finish();
  }, [step, phase, finish]);

  const back = useCallback(() => {
    if (step === 1 && phase > 0) {
      setPhase(p => p - 1);
      return;
    }
    if (step === 1) {
      setStep(0);
      return;
    }
  }, [step, phase]);

  if (!open) return null;

  const isLesson = step === 1;
  const isLastPhase = phase >= 2;
  const ctaLabel = !isLesson
    ? t('tour_next', lang)
    : (isLastPhase ? t('tour_begin', lang) : t('tour_next', lang));

  const eyebrow = !isLesson
    ? t('tour_step1_eyebrow', lang)
    : t('tour_lesson_eyebrow', lang);

  const titleKey = !isLesson
    ? 'tour_step1_title'
    : `tour_phase${phase}_title`;

  const bodyKey = !isLesson
    ? 'tour_step1_body'
    : `tour_phase${phase}_body`;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('tour_aria', lang)}
      style={{
        position: 'fixed', inset: 0, zIndex: 700,
        background: 'rgba(28, 14, 22, 0.58)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        paddingTop: 'max(20px, env(safe-area-inset-top))',
        paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
        paddingLeft: 'max(20px, env(safe-area-inset-left))',
        paddingRight: 'max(20px, env(safe-area-inset-right))',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'bb-tour-fade 320ms ease-out',
      }}
    >
      <div style={{
        width: '100%',
        maxWidth: 380,
        background:
          'linear-gradient(180deg, #fff 0%, var(--primary-pale, #fbeae7) 130%)',
        borderRadius: 26,
        padding: '28px 24px 24px',
        boxShadow: '0 30px 80px -20px rgba(45,31,38,.45)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        textAlign: 'center',
      }}>
        {!isLesson
          ? <WelcomeBloom />
          : <AnimatedLesson
              phase={phase}
              paused={paused || reducedMotion}
              reducedMotion={reducedMotion}
              onTap={() => {
                // Tap to pause / resume rather than advance: a slow
                // reader needs a way to hold a phase. Manual
                // advancement uses the Next button.
                if (reducedMotion) return;
                setPaused(p => !p);
              }}
            />
        }

        <div style={{
          font: '600 10px/1 var(--font-ui, Manrope)',
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: 'var(--primary-dark, #a85c4b)',
        }}>
          {eyebrow}
        </div>

        <div
          key={`${step}-${phase}-t`}
          style={{
            font: '500 22px/1.2 var(--font-title, Fraunces), serif',
            color: 'var(--ink, #2d1f26)',
            letterSpacing: '-0.005em',
            textWrap: 'balance',
            animation: 'bb-tour-cap 420ms ease-out',
          }}
        >
          {t(titleKey, lang)}
        </div>

        <div
          key={`${step}-${phase}-b`}
          style={{
            font: '400 14px/1.45 var(--font-body, Manrope)',
            color: 'var(--ink-dim, #6b5560)',
            maxWidth: 300,
            textWrap: 'balance',
            animation: 'bb-tour-cap 420ms 60ms ease-out backwards',
          }}
        >
          {t(bodyKey, lang)}
        </div>

        {/* Progress dots — outer row tracks the two main steps; on
            the lesson step a smaller inner row of three dots tracks
            the animation phase. */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, marginTop: 4 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {Array.from({ length: total }).map((_, i) => (
              <div
                key={i}
                aria-hidden="true"
                style={{
                  width: i === step ? 18 : 6,
                  height: 6,
                  borderRadius: 999,
                  background: i === step
                    ? 'var(--primary, #c97a68)'
                    : 'color-mix(in srgb, var(--primary, #c97a68) 22%, transparent)',
                  transition: 'width 240ms ease, background 240ms ease',
                }}
              />
            ))}
          </div>
          {isLesson && (
            <div style={{ display: 'flex', gap: 4 }}>
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  aria-hidden="true"
                  style={{
                    width: 4, height: 4, borderRadius: 999,
                    background: i <= phase
                      ? 'color-mix(in srgb, var(--primary, #c97a68) 70%, transparent)'
                      : 'color-mix(in srgb, var(--primary, #c97a68) 18%, transparent)',
                    transition: 'background 240ms ease',
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, width: '100%', marginTop: 6 }}>
          <button
            type="button"
            onClick={step === 0 ? later : back}
            style={{
              flex: 1,
              minHeight: 46,
              borderRadius: 999,
              border: '1px solid color-mix(in srgb, var(--ink, #2d1f26) 10%, transparent)',
              background: 'transparent',
              color: 'var(--ink-dim, #6b5560)',
              font: '600 13px/1 var(--font-ui, Manrope)',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {step === 0 ? t('tour_later', lang) : t('tour_back', lang)}
          </button>
          {isLesson && !isLastPhase && (
            <button
              type="button"
              onClick={finish}
              style={{
                flex: 1,
                minHeight: 46,
                borderRadius: 999,
                border: '1px solid color-mix(in srgb, var(--ink, #2d1f26) 10%, transparent)',
                background: 'transparent',
                color: 'var(--ink-dim, #6b5560)',
                font: '600 13px/1 var(--font-ui, Manrope)',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {t('tour_skip', lang)}
            </button>
          )}
          <button
            type="button"
            onClick={next}
            style={{
              flex: 1.6,
              minHeight: 46,
              borderRadius: 999,
              border: 'none',
              background:
                'linear-gradient(180deg, var(--primary-light, #e3a193) 0%, var(--primary, #c97a68) 100%)',
              color: '#fff',
              font: '600 14px/1 var(--font-ui, Manrope)',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              boxShadow: '0 10px 24px -10px color-mix(in srgb, var(--primary, #c97a68) 55%, transparent)',
            }}
          >
            {ctaLabel}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bb-tour-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes bb-tour-cap {
          0%   { opacity: 0; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes bb-tour-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/* ── Step 1: welcome bloom ─────────────────────────────────────── */

function WelcomeBloom() {
  return (
    <svg width={180} height={180} viewBox="0 0 180 180" aria-hidden="true"
      style={{ display: 'block' }}>
      <defs>
        <radialGradient id="tcWelcomeBloom" cx="50%" cy="50%" r="60%">
          <stop offset="0%"  stopColor="#fbd6cb" />
          <stop offset="60%" stopColor="#e3a193" />
          <stop offset="100%" stopColor="#a85c4b" />
        </radialGradient>
        <radialGradient id="tcWelcomeHalo" cx="50%" cy="50%" r="55%">
          <stop offset="0%"  stopColor="#fbeae7" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#fbeae7" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="90" cy="90" r="80" fill="url(#tcWelcomeHalo)" />
      <g transform="translate(90 90)">
        {Array.from({ length: 8 }).map((_, i) => (
          <ellipse
            key={i}
            cx="0" cy="-22"
            rx="11" ry="26"
            transform={`rotate(${i * 45})`}
            fill="url(#tcWelcomeBloom)"
            opacity="0.92"
          />
        ))}
        <circle r="9" fill="#a85c4b" />
      </g>
    </svg>
  );
}

/* ── Step 2: animated lesson (canal → +flower → +breath line) ── */

function AnimatedLesson({ phase, paused, reducedMotion, onTap }) {
  const showFlower = phase >= 1;
  const showBreath = phase >= 2;
  return (
    <button
      type="button"
      onClick={onTap}
      aria-hidden="true"
      tabIndex={-1}
      style={{
        position: 'relative',
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: reducedMotion ? 'default' : 'pointer',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <svg width={180} height={180} viewBox="0 0 180 180"
        style={{ display: 'block' }}>
        <defs>
          <linearGradient id="tcLessonBand" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"  stopColor="#fbeae7" />
            <stop offset="100%" stopColor="#e3a193" stopOpacity="0.55" />
          </linearGradient>
          <radialGradient id="tcLessonFlower" cx="50%" cy="40%" r="60%">
            <stop offset="0%"  stopColor="#fbd6cb" />
            <stop offset="100%" stopColor="#a85c4b" />
          </radialGradient>
          <clipPath id="tcLessonClip">
            <path d="M 73 20 Q 90 8 107 20 C 118 30 124 80 124 95 C 124 130 116 158 90 164 C 64 158 56 130 56 95 C 56 80 62 30 73 20 Z" />
          </clipPath>
        </defs>

        {/* Canal — always visible */}
        <path
          d="M 73 20 Q 90 8 107 20 C 118 30 124 80 124 95 C 124 130 116 158 90 164 C 64 158 56 130 56 95 C 56 80 62 30 73 20 Z"
          fill="url(#tcLessonBand)" stroke="#c97a68" strokeWidth="1.4" opacity="0.95"
        />

        {/* Centre line — fades in with the flower */}
        <line
          x1="90" y1="22" x2="90" y2="162"
          stroke="#c97a68" strokeOpacity="0.35" strokeWidth="1"
          style={{
            opacity: showFlower ? 1 : 0,
            transition: 'opacity 600ms ease',
          }}
        />

        {/* Flower — fades in at phase 1, drifts gently up/down to
            evoke a contraction cycle */}
        <g
          transform="translate(90 48)"
          style={{
            opacity: showFlower ? 1 : 0,
            transformBox: 'fill-box',
            transformOrigin: 'center',
            transition: 'opacity 600ms ease',
          }}
        >
          {showFlower && (
            <g style={{
              animation: reducedMotion
                ? 'none'
                : 'bb-tour-flower-bob 3.2s ease-in-out infinite',
              animationPlayState: paused ? 'paused' : 'running',
            }}>
              <ellipse cx="0" cy="-8" rx="5" ry="10" fill="url(#tcLessonFlower)" />
              <ellipse cx="0" cy="-8" rx="5" ry="10" transform="rotate(60)"  fill="url(#tcLessonFlower)" />
              <ellipse cx="0" cy="-8" rx="5" ry="10" transform="rotate(120)" fill="url(#tcLessonFlower)" />
              <ellipse cx="0" cy="-8" rx="5" ry="10" transform="rotate(180)" fill="url(#tcLessonFlower)" />
              <ellipse cx="0" cy="-8" rx="5" ry="10" transform="rotate(240)" fill="url(#tcLessonFlower)" />
              <ellipse cx="0" cy="-8" rx="5" ry="10" transform="rotate(300)" fill="url(#tcLessonFlower)" />
              <circle r="3.5" fill="#a85c4b" />
            </g>
          )}
        </g>

        {/* Breath line — animates from low to high inside the canal */}
        {showBreath && (
          <g clipPath="url(#tcLessonClip)" style={{ animation: 'bb-tour-fade-in 500ms ease-out' }}>
            {reducedMotion ? (
              <rect x="30" y="80" width="130" height="3"
                fill="#87987c"
                style={{ filter: 'drop-shadow(0 0 4px rgba(135,152,124,0.6))' }} />
            ) : (
              <rect x="30" y="47" width="130" height="3"
                fill="#87987c"
                style={{ filter: 'drop-shadow(0 0 4px rgba(135,152,124,0.6))' }}>
                {!paused && (
                  <animate attributeName="y" values="158;47;158" dur="4s" repeatCount="indefinite" />
                )}
              </rect>
            )}
          </g>
        )}
      </svg>
      {/* Pause indicator — small badge on the figure when the user
          has tapped to pause auto-advance. Aria-hidden because
          the surrounding button is. */}
      {paused && !reducedMotion && (
        <span aria-hidden="true" style={{
          position: 'absolute',
          top: 8, right: 8,
          padding: '4px 10px',
          borderRadius: 999,
          background: 'rgba(45,31,38,0.78)',
          color: '#fff',
          font: '600 10px/1 var(--font-ui, Manrope)',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}>
          {'❚❚'}
        </span>
      )}
      <style>{`
        @keyframes bb-tour-flower-bob {
          0%   { transform: translateY(0); }
          50%  { transform: translateY(8px); }
          100% { transform: translateY(0); }
        }
      `}</style>
    </button>
  );
}
